import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  CollapsibleSection,
  type CollapsibleSectionProps,
  ColumnManager,
  type ColumnFilterOption,
  type ColumnFilterState,
  EmptyState,
  FilterableTH,
  type FilterableTHProps,
  FilterSheet,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  THead,
  TR,
} from '@/components/ui';
import { EMPTY_FILTER, hasFilterInput, isFilterActive } from '@/components/ui/ColumnFilter';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
import { ChevronIcon } from '@/components/catalog/icons';
import { ApiError, api } from '@/lib/api';
import { currentCycle, todayLocal } from '@/lib/document-naming';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { formatDateOnly, pluralise } from '@/lib/format';
import type { ClientDeliverable, Cycle } from '@/lib/types';
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';
import { BillingStatusCell, deliveryState } from '@/pages/ClientDeliverablesPage';

/**
 * The company page's "Services" section (owner rulings 2026-10-18): this
 * company's Client Deliverables rows, one foldable table per cycle, newest
 * cycle first.
 *
 * Data is the tracker's own list in ONE read —
 * `GET /client-deliverables?companyId&sort=project&order=asc`, no `cycleId`, so
 * every cycle comes back — grouped here by each row's own `cycle`. Own-scope
 * narrowing and the derived billing status are the server's, exactly as the
 * tracker shows them. No new endpoint.
 *
 * ⚠️ The CALLER gates this on `deliverables` read and mounts nothing without
 * it, so a reader who may not see the tracker never sends a request. A 403 that
 * arrives anyway (a grant revoked mid-session) renders as the danger notice.
 *
 * ## Cycle groups and folding
 *
 * Groups are ordered by the cycle's START DATE, newest first — never by id,
 * cycle ids are non-contiguous. The current cycle (the one whose window holds
 * today, `currentCycle`) is always listed, empty or not, and is the ONLY group
 * open on arrival; every other cycle — 2025-26 and everything before it, and
 * any future cycle — arrives folded. When no cycle holds today, the newest group
 * opens instead. Folds are session state only: nothing is stored. Rows whose
 * cycle is unknown sit last under "No cycle".
 *
 * ## Columns and filters (one set for the whole section)
 *
 * ONE `useColumnLayout` scope, `company-services`, drives every cycle table AND
 * the stacked cards below `md` — a column hidden in the manager is gone from
 * both. Service is locked: a row of figures with no name describes nothing.
 *
 * ONE remembered funnel set (`company-services-filters`, the same account-level
 * memory the Sales & Billing tabs use) filters the LOADED rows client-side
 * across every cycle. Text columns get the funnel's searchable value list;
 * Delivery status and Billing are a value pick. A cycle's count is its FILTERED
 * row count, so a cycle with no match still shows its header with 0. Below `md`
 * the same state is reached through `FilterSheet`.
 *
 * ## Width
 *
 * ⚠️ *SUPERSEDED: a fixed `min-w-[72rem]` floor.* At the company page's width
 * that floor pushed the last column past the scroller's edge ("Not…" for
 * Notes). The floor is now COMPUTED from the visible columns and deliberately
 * small, and the text cells wrap, so the auto layout fits the row into the
 * section; only a genuinely narrow viewport scrolls sideways, inside the same
 * pinned-header scroller.
 *
 * The billing badge and the delivery state are IMPORTED from the tracker page
 * (`BillingStatusCell`, `deliveryState`) so the two screens cannot drift.
 * Inclusion rows have no delivery state of their own on the tracker's terms,
 * so they show a dash there.
 *
 * Read-only by design: every edit happens on the tracker, which the link under
 * the tables opens filtered to this company.
 */

const PER_PAGE = 100;
/** A runaway guard, not a limit anybody should meet: 2,000 rows. */
const MAX_PAGES = 20;

const LAYOUT_SCOPE = 'company-services';
const FILTER_MEMORY_ID = 'company-services-filters';

type ColumnKey =
  | 'service'
  | 'where'
  | 'promised'
  | 'delivered'
  | 'status'
  | 'dates'
  | 'specialist'
  | 'billing'
  | 'notes'
  | 'billingNote';

const COLUMN_KEYS: readonly ColumnKey[] = [
  'service',
  'where',
  'promised',
  'delivered',
  'status',
  'dates',
  'specialist',
  'billing',
  'notes',
  'billingNote',
];

const LOCKED_KEYS: readonly ColumnKey[] = ['service'];

const COLUMN_LABELS: Record<ColumnKey, string> = {
  service: 'Service',
  where: 'Cities / regions',
  promised: 'Promised',
  delivered: 'Delivered',
  status: 'Delivery status',
  dates: 'Dates',
  specialist: 'Specialist',
  billing: 'Billing',
  notes: 'Notes',
  billingNote: 'Billing note',
};

/**
 * The FLOOR per column, in px. Requests, not widths: the table is auto layout,
 * so a column grows past this when there is room and its text wraps when there
 * is not. Summed over the VISIBLE columns only — a floor that counted a hidden
 * column would leave a sideways scroll over a table that now fits.
 */
const COLUMN_MIN_PX: Record<ColumnKey, number> = {
  // Doubled on the owner's request (2026-10-18), and held on the cells below.
  service: 300,
  where: 220,
  promised: 72,
  delivered: 76,
  status: 96,
  dates: 96,
  specialist: 90,
  billing: 104,
  notes: 120,
  billingNote: 110,
};

const NUMERIC_KEYS: ReadonlySet<ColumnKey> = new Set(['promised', 'delivered']);

/** Columns whose floor is held on the cell itself, so auto layout cannot squeeze them. */
const WIDE_KEYS: ReadonlySet<ColumnKey> = new Set(['service', 'where']);

/**
 * The billing funnel's option labels, keyed on `billing.status` — never on the
 * admin-editable `invoiceStatusName`.
 *
 * ⚠️ **A COPY of the tracker's private `BILLING_LABELS`, knowingly.** That map
 * is not exported from `ClientDeliverablesPage`, and this pass may not edit that
 * file. Typed as a `Record` over the wire union, so a status added there fails
 * the build here rather than rendering a raw slug. Export the tracker's map and
 * import it here when that page is next open.
 */
type BillingStatus = ClientDeliverable['billing']['status'];
const BILLING_FILTER_LABELS: Record<BillingStatus, string> = {
  none: 'No document',
  not_billable: 'Not billable',
  quote_sent: 'Quote sent',
  awaiting_invoice: 'Awaiting invoice',
  invoiced: 'Invoiced',
  cancelled: 'Cancelled',
  paid: 'Paid',
};

interface CycleGroup {
  /** The cycle id as a string, or `'none'` for rows with no cycle. */
  key: string;
  name: string;
  isCurrent: boolean;
  rows: ClientDeliverable[];
}

type Load =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ok'; groups: CycleGroup[] };

async function loadCompanyRows(
  companyId: number,
  signal: AbortSignal,
): Promise<ClientDeliverable[]> {
  const rows: ClientDeliverable[] = [];
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const response = await api.listClientDeliverables(
      { companyId, page, perPage: PER_PAGE, sort: 'project', order: 'asc' },
      signal,
    );
    rows.push(...response.data);
    if (page >= response.meta.totalPages) break;
  }
  return rows;
}

/**
 * Rows grouped by their cycle, newest start date first. The current cycle is
 * always present; any other cycle only when it has rows. A row naming a cycle
 * the cycles list does not hold sorts after the known ones, then "No cycle".
 */
function groupByCycle(
  rows: readonly ClientDeliverable[],
  cycles: readonly Cycle[],
  today = todayLocal(),
): CycleGroup[] {
  const current = currentCycle(cycles, today);
  const byId = new Map(cycles.map((cycle) => [cycle.id, cycle]));
  const groups = new Map<string, CycleGroup>();
  const startOf = new Map<string, string>();

  if (current !== null) {
    groups.set(String(current.id), {
      key: String(current.id),
      name: current.cycleName,
      isCurrent: true,
      rows: [],
    });
    startOf.set(String(current.id), current.startDate);
  }

  for (const row of rows) {
    const key = row.cycle === null ? 'none' : String(row.cycle.id);
    let group = groups.get(key);
    if (group === undefined) {
      const known = row.cycle === null ? undefined : byId.get(row.cycle.id);
      group = {
        key,
        name: known?.cycleName ?? row.cycle?.name ?? 'No cycle',
        isCurrent: false,
        rows: [],
      };
      groups.set(key, group);
      if (known !== undefined) startOf.set(key, known.startDate);
    }
    group.rows.push(row);
  }

  return Array.from(groups.values()).sort((a, b) => {
    if (a.key === 'none' || b.key === 'none') return a.key === 'none' ? 1 : -1;
    const startA = startOf.get(a.key);
    const startB = startOf.get(b.key);
    if (startA === undefined || startB === undefined) {
      if (startA === startB) return a.name.localeCompare(b.name);
      return startA === undefined ? 1 : -1;
    }
    return startB.localeCompare(startA);
  });
}

function messageOf(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error && error.message.trim() !== '') return error.message;
  return 'The services could not be loaded.';
}

function isInclusion(row: ClientDeliverable): boolean {
  return row.source === 'quote_inclusion' || row.source === 'invoice_inclusion';
}

function statusOf(row: ClientDeliverable): string {
  return isInclusion(row) ? '—' : deliveryState(row);
}

function namesOf(items: readonly { name: string }[]): string | null {
  return items.length === 0 ? null : items.map((item) => item.name).join(', ');
}

function textOrNull(value: string | null | undefined): string | null {
  return value === null || value === undefined || value.trim() === '' ? null : value;
}

function datesOf(row: ClientDeliverable): string {
  if (row.startDate === null && row.endDate === null) return '—';
  if (row.endDate === null || row.endDate === row.startDate) return formatDateOnly(row.startDate);
  return `${formatDateOnly(row.startDate)} – ${formatDateOnly(row.endDate)}`;
}

/* --- filters ------------------------------------------------------------- */

interface FilterColumn {
  readonly key: ColumnKey;
  /** Every filterable value the row holds; `[]` counts as EMPTY. */
  readonly values: (row: ClientDeliverable) => string[];
  /** A value's label in the list; defaults to the value. */
  readonly format?: (value: string) => string;
}

function one(value: string | null | undefined): string[] {
  const text = textOrNull(value);
  return text === null ? [] : [text];
}

const FILTER_COLUMNS: readonly FilterColumn[] = [
  { key: 'service', values: (row) => one(row.product?.productName) },
  {
    key: 'where',
    values: (row) => [
      ...row.cities.map((city) => city.name),
      ...row.regions.map((region) => region.name),
    ],
  },
  { key: 'status', values: (row) => (isInclusion(row) ? [] : [deliveryState(row)]) },
  { key: 'specialist', values: (row) => one(row.specialist?.name) },
  {
    key: 'billing',
    values: (row) => [row.billing.status],
    format: (value) => BILLING_FILTER_LABELS[value as BillingStatus] ?? value,
  },
  { key: 'notes', values: (row) => one(row.lineNote) },
  { key: 'billingNote', values: (row) => one(row.billingNote) },
];

const FILTER_BY_KEY = new Map<string, FilterColumn>(
  FILTER_COLUMNS.map((column) => [column.key, column]),
);

type Filters = Record<string, ColumnFilterState>;

/** A remembered funnel set: a plain object of filter states, nothing else. */
function parseFilters(raw: unknown): Filters | undefined {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const out: Filters = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (
      FILTER_BY_KEY.has(key) &&
      value !== null &&
      typeof value === 'object' &&
      Array.isArray((value as ColumnFilterState).selected) &&
      typeof (value as ColumnFilterState).emptiness === 'string'
    ) {
      out[key] = value as ColumnFilterState;
    }
  }
  return out;
}

function passes(
  column: FilterColumn,
  state: ColumnFilterState | undefined,
  row: ClientDeliverable,
): boolean {
  if (!state) return true;
  const values = column.values(row);
  if (state.emptiness === 'empty' && values.length > 0) return false;
  if (state.emptiness === 'notEmpty' && values.length === 0) return false;
  if (state.selected.length === 0) return true;
  return values.some((value) => state.selected.includes(value));
}

function optionsOf(column: FilterColumn, rows: readonly ClientDeliverable[]): ColumnFilterOption[] {
  const counts = new Map<string, number>();
  for (const row of rows) {
    for (const value of new Set(column.values(row))) {
      counts.set(value, (counts.get(value) ?? 0) + 1);
    }
  }
  return Array.from(counts, ([value, count]) => ({
    value,
    label: column.format?.(value) ?? value,
    count,
  })).sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }),
  );
}

/* --- cells --------------------------------------------------------------- */

function Dash() {
  return <span className="text-xs text-fg-subtle">—</span>;
}

function ServiceCell({ row }: { row: ClientDeliverable }) {
  const product = row.product?.productName ?? null;
  const project = row.project?.name ?? null;
  return (
    <div className="min-w-0">
      {product === null ? <Dash /> : <span className="block font-medium text-fg">{product}</span>}
      {project !== null && <span className="block text-xs text-fg-muted">{project}</span>}
    </div>
  );
}

function WhereCell({ row }: { row: ClientDeliverable }) {
  const cities = namesOf(row.cities);
  const regions = namesOf(row.regions);
  if (cities === null && regions === null) return <Dash />;
  return (
    <div className="min-w-0">
      {cities !== null && <span className="block">{cities}</span>}
      {regions !== null && <span className="block text-xs text-fg-muted">{regions}</span>}
    </div>
  );
}

function NoteText({ value, muted = false }: { value: string | null | undefined; muted?: boolean }) {
  const text = textOrNull(value);
  if (text === null) return <Dash />;
  return (
    <span
      className={
        muted
          ? 'block whitespace-pre-line text-xs text-fg-muted [overflow-wrap:anywhere]'
          : 'block whitespace-pre-line [overflow-wrap:anywhere]'
      }
    >
      {text}
    </span>
  );
}

function Specialist({ row }: { row: ClientDeliverable }) {
  return row.specialist === null ? <Dash /> : <span>{row.specialist.name}</span>;
}

/** One cell's content — the table and the cards render the same thing. */
function cellContent(key: ColumnKey, row: ClientDeliverable): ReactNode {
  switch (key) {
    case 'service':
      return <ServiceCell row={row} />;
    case 'where':
      return <WhereCell row={row} />;
    case 'promised':
      return <span className="tabular">{row.promised}</span>;
    case 'delivered':
      return <span className="tabular">{row.completed}</span>;
    case 'status':
      return statusOf(row);
    case 'dates':
      return <span className="tabular">{datesOf(row)}</span>;
    case 'specialist':
      return <Specialist row={row} />;
    case 'billing':
      return <BillingStatusCell row={row} />;
    case 'notes':
      return <NoteText value={row.lineNote} />;
    case 'billingNote':
      return <NoteText value={row.billingNote} muted />;
  }
}

function isColumnKey(key: string): key is ColumnKey {
  return (COLUMN_KEYS as readonly string[]).includes(key);
}

/* --- one cycle's table and cards ----------------------------------------- */

interface ViewProps {
  rows: ClientDeliverable[] | null;
  visibleKeys: readonly ColumnKey[];
  headerFor: (key: ColumnKey) => FilterableTHProps;
  frozenSideOf: (key: string) => 'left' | 'right' | undefined;
}

function ServicesTable({ rows, visibleKeys, headerFor, frozenSideOf }: ViewProps) {
  const headRowRef = useRef<HTMLTableRowElement>(null);
  const offsets = useFrozenOffsets(headRowRef, visibleKeys, frozenSideOf);
  const frozenPropsOf = (key: ColumnKey): { frozen?: 'left' | 'right'; frozenOffset?: number } => {
    const side = frozenSideOf(key);
    return side === undefined ? {} : { frozen: side, frozenOffset: offsets[key] ?? 0 };
  };
  const minWidth = visibleKeys.reduce((total, key) => total + COLUMN_MIN_PX[key], 0);

  return (
    <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
      <Table style={{ minWidth }}>
        <THead>
          <TR ref={headRowRef}>
            {visibleKeys.map((key) => (
              <FilterableTH key={key} {...headerFor(key)} {...frozenPropsOf(key)} />
            ))}
          </TR>
        </THead>
        <TBody>
          {rows === null
            ? Array.from({ length: 3 }, (_, index) => (
                <TR key={index}>
                  {visibleKeys.map((key) => (
                    <TD key={key} numeric={NUMERIC_KEYS.has(key)} {...frozenPropsOf(key)}>
                      <Skeleton className={NUMERIC_KEYS.has(key) ? 'ml-auto h-4 w-8' : 'h-4 w-20'} />
                    </TD>
                  ))}
                </TR>
              ))
            : rows.map((row) => (
                <TR key={row.key}>
                  {visibleKeys.map((key) => (
                    <TD
                      key={key}
                      verticalAlign="top"
                      numeric={NUMERIC_KEYS.has(key)}
                      className={key === 'status' ? 'whitespace-nowrap' : undefined}
                      style={WIDE_KEYS.has(key) ? { minWidth: COLUMN_MIN_PX[key] } : undefined}
                      {...frozenPropsOf(key)}
                    >
                      {cellContent(key, row)}
                    </TD>
                  ))}
                </TR>
              ))}
        </TBody>
      </Table>
    </TableWrap>
  );
}

function CardField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium text-fg-muted">{label}</dt>
      <dd className="m-0 mt-0.5 min-w-0 [overflow-wrap:anywhere]">{children}</dd>
    </div>
  );
}

/** Columns that take the card's full width rather than half of it. */
const WIDE_CARD_KEYS: ReadonlySet<ColumnKey> = new Set(['where', 'notes', 'billingNote']);

function ServicesCards({ rows, visibleKeys }: Pick<ViewProps, 'rows' | 'visibleKeys'>) {
  if (rows === null) {
    return (
      <ul className="m-0 grid list-none gap-2 p-0">
        {Array.from({ length: 2 }, (_, index) => (
          <li key={index} className="rounded-md border border-border p-3">
            <Skeleton className="h-4 w-40" />
            <div className="mt-2.5 grid gap-1.5">
              <Skeleton className="h-2.5 w-32" />
              <Skeleton className="h-2.5 w-24" />
            </div>
          </li>
        ))}
      </ul>
    );
  }
  // Service is the card's title and is locked, so it is never in the field grid.
  const fieldKeys = visibleKeys.filter((key) => key !== 'service');
  return (
    <ul className="m-0 grid list-none gap-2 p-0">
      {rows.map((row) => (
        <li key={row.key} className="rounded-md border border-border p-3">
          <ServiceCell row={row} />
          {fieldKeys.length > 0 && (
            <dl className="m-0 mt-2 grid grid-cols-2 gap-x-3 gap-y-2">
              {fieldKeys.map((key) => (
                <div key={key} className={WIDE_CARD_KEYS.has(key) ? 'col-span-2' : undefined}>
                  <CardField label={COLUMN_LABELS[key]}>{cellContent(key, row)}</CardField>
                </div>
              ))}
            </dl>
          )}
        </li>
      ))}
    </ul>
  );
}

interface CycleBlockProps extends ViewProps {
  id: string;
  title: string | null;
  open: boolean;
  onToggle: () => void;
  /** The FILTERED row count; `null` while loading. */
  count: number | null;
  filtered: boolean;
}

function CycleBlock({
  id,
  title,
  open,
  onToggle,
  count,
  filtered,
  rows,
  visibleKeys,
  headerFor,
  frozenSideOf,
}: CycleBlockProps) {
  const bodyId = `${id}-body`;
  return (
    <div className="border-b border-border px-4 py-2 last:border-b-0">
      <h3 className="m-0 text-sm font-semibold text-fg">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={bodyId}
          className="-mx-2"
          leadingIcon={<ChevronIcon direction={open ? 'down' : 'right'} />}
        >
          <span className="flex items-baseline gap-2">
            {title === null ? <Skeleton className="h-4 w-20" /> : <span className="text-fg">{title}</span>}
            {count !== null && (
              <span className="text-xs font-normal text-fg-muted tabular">{count}</span>
            )}
          </span>
        </Button>
      </h3>
      {open && (
        <div id={bodyId} className="mt-2 pb-2">
          {rows !== null && rows.length === 0 ? (
            <EmptyState
              title={filtered ? 'No services match these filters' : 'No services in this cycle'}
              className="py-6"
            />
          ) : (
            <>
              <div className="max-md:hidden">
                <ServicesTable
                  rows={rows}
                  visibleKeys={visibleKeys}
                  headerFor={headerFor}
                  frozenSideOf={frozenSideOf}
                />
              </div>
              <div className="md:hidden">
                <ServicesCards rows={rows} visibleKeys={visibleKeys} />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* --- the section --------------------------------------------------------- */

export interface CompanyServicesSectionProps {
  companyId: number;
  shell: Omit<CollapsibleSectionProps, 'title' | 'children' | 'summary'>;
}

export function CompanyServicesSection({ companyId, shell }: CompanyServicesSectionProps) {
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  /** Explicit fold choices this session, by group key; absent = the default. */
  const [folds, setFolds] = useState<Record<string, boolean>>({});
  const [filters, setFilters] = useRememberedState<Filters>(FILTER_MEMORY_ID, {}, parseFilters);
  const layout = useColumnLayout(LAYOUT_SCOPE, COLUMN_KEYS, LOCKED_KEYS);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    Promise.all([api.listCycles({ page: 1, perPage: 100 }, signal), loadCompanyRows(companyId, signal)])
      .then(([cycles, rows]) => {
        if (signal.aborted) return;
        setLoad({ kind: 'ok', groups: groupByCycle(rows, cycles.data) });
      })
      .catch((error: unknown) => {
        if (signal.aborted) return;
        setLoad({ kind: 'error', message: messageOf(error) });
      });
    return () => controller.abort();
  }, [companyId]);

  const allRows = useMemo(
    () => (load.kind === 'ok' ? load.groups.flatMap((group) => group.rows) : []),
    [load],
  );

  const activeColumns = FILTER_COLUMNS.filter((column) => {
    const state = filters[column.key];
    return state !== undefined && isFilterActive(state);
  });
  const filtered = activeColumns.length > 0;
  const keep = (row: ClientDeliverable) =>
    activeColumns.every((column) => passes(column, filters[column.key], row));

  const setFilter = (key: string, next: ColumnFilterState) =>
    setFilters((current) => {
      if (hasFilterInput(next)) return { ...current, [key]: next };
      if (!(key in current)) return current;
      const rest: Filters = {};
      for (const [name, value] of Object.entries(current)) {
        if (name !== key) rest[name] = value;
      }
      return rest;
    });

  const headerFor = (key: ColumnKey): FilterableTHProps => {
    const column = FILTER_BY_KEY.get(key);
    const label = COLUMN_LABELS[key];
    if (column === undefined) return { label, numeric: NUMERIC_KEYS.has(key) };
    return {
      label,
      numeric: NUMERIC_KEYS.has(key),
      filter: {
        label,
        options: optionsOf(column, allRows),
        value: filters[key] ?? EMPTY_FILTER,
        scope: 'page',
        pageRowCount: allRows.length,
        disabled: load.kind !== 'ok',
        onChange: (next) => setFilter(key, next),
      },
    };
  };

  const visibleKeys = layout.visibleKeys.filter(isColumnKey);
  const total = load.kind === 'ok' ? allRows.length : null;
  const shown = load.kind === 'ok' ? allRows.filter(keep).length : null;

  const isOpen = (group: CycleGroup, index: number, groups: readonly CycleGroup[]): boolean => {
    const chosen = folds[group.key];
    if (chosen !== undefined) return chosen;
    const hasCurrent = groups.some((candidate) => candidate.isCurrent);
    return hasCurrent ? group.isCurrent : index === 0;
  };

  const toolbar = (
    <div className="flex flex-wrap items-center justify-end gap-2 px-4 pt-3">
      {filtered && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="max-md:hidden"
          onClick={() => setFilters({})}
        >
          Clear filters
        </Button>
      )}
      <FilterSheet
        className="md:hidden"
        keys={visibleKeys}
        headerFor={(key) => headerFor(key as ColumnKey)}
        activeCount={activeColumns.length}
        clearAll={() => setFilters({})}
      />
      <ColumnManager
        layout={layout}
        labelOf={(key) => (isColumnKey(key) ? COLUMN_LABELS[key] : key)}
        lockedKeys={LOCKED_KEYS}
        lockReasonOf={() => 'The service names the row'}
      />
    </div>
  );

  let body: ReactNode;
  if (load.kind === 'loading') {
    body = (
      <CycleBlock
        id={`${shell.id}-loading`}
        title={null}
        open
        onToggle={() => undefined}
        count={null}
        filtered={false}
        rows={null}
        visibleKeys={visibleKeys}
        headerFor={headerFor}
        frozenSideOf={layout.frozenSideOf}
      />
    );
  } else if (load.kind === 'error') {
    body = (
      <div className="px-4 py-4">
        <Notice tone="danger">{load.message}</Notice>
      </div>
    );
  } else if (load.groups.length === 0 || total === 0) {
    body = <EmptyState title="No services for this company" />;
  } else {
    body = (
      <>
        {toolbar}
        {load.groups.map((group, index, groups) => {
          const rows = filtered ? group.rows.filter(keep) : group.rows;
          const open = isOpen(group, index, groups);
          return (
            <CycleBlock
              key={group.key}
              id={`${shell.id}-cycle-${group.key}`}
              title={group.name}
              open={open}
              onToggle={() => setFolds((current) => ({ ...current, [group.key]: !open }))}
              count={rows.length}
              filtered={filtered}
              rows={rows}
              visibleKeys={visibleKeys}
              headerFor={headerFor}
              frozenSideOf={layout.frozenSideOf}
            />
          );
        })}
      </>
    );
  }

  const summary =
    total === null || shown === null
      ? undefined
      : filtered
        ? `${shown} of ${pluralise(total, 'service')}`
        : pluralise(total, 'service');

  return (
    <CollapsibleSection {...shell} title="Services" summary={summary}>
      {body}
      <div className="border-t border-border px-4 py-3">
        <TextLink
          to={`/client-deliverables?companyId=${companyId}`}
          className="text-sm font-medium"
        >
          Open in Client Deliverables
        </TextLink>
      </div>
    </CollapsibleSection>
  );
}
